import { useEffect } from "react";
import { matchPath, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { format } from "date-fns";
import {
    FormikFromProps,
    FormikProductProps,
    initialFromValues,
    initialProductValues,
    initialUpdateToValues,
    orderFromValidator,
    orderToValidator,
    UpdateFormikToProps,
} from "./-components/formik-utils";
import { useGetOrderInfo } from "common/hooks/user/order/useGetOrderInfo";
import { errorMsgHandler, format12To24, formatDeliveryTime, formatDisplayDateStringToDate } from "common/hooks/utils/utils";
import { OrderPutRequest, usePutOrderInfo } from "common/hooks/user/order/usePutOrderInfo";
import { useGetProductInfo } from "common/hooks/user/Products/useGetProductInfo";
import { useGetCommonSender } from "common/hooks/common/useGetCommonSender";
import { useAuthStore } from "user/store/useAuthStore";

export const updateRootPages = ["/orders/:idx/update"];
export const updatePages = [
    "/orders/:idx/update",
    "/orders/:idx/update/from",
    "/orders/:idx/update/select-category",
    "/orders/:idx/update/select-product",
    "/orders/:idx/update/product-detail/:idx",
    "/orders/:idx/update/to",
];

export default function UpdateOrder() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const orderIdx = params.idx;
    const pathname = location.pathname;
    const { data } = useGetOrderInfo(Number(orderIdx));
    const { mutate, isPending } = usePutOrderInfo(Number(params.idx));
    const user = useAuthStore(state => state);
    const isIndividualCustomer = user?.m_category === 2; // 개인 회원
    const customer_idx = data?.member_info.customer_idx;
    const product_idx = data?.product_info.product_idx ?? null;
    const { data: productInfo } = useGetProductInfo(Number(product_idx));
    const category_idx = productInfo?.product_category.product_category_idx;
    const isMatchedRootPages = updateRootPages.some(page => matchPath(page, pathname));
    // console.log("UpdateOrder data", data);
    // console.log("productInfo", productInfo);

    const formik = {
        from: useFormik<FormikFromProps>({
            initialValues: initialFromValues,
            onSubmit: values => {
                navigate("to");
            },
            validate: orderFromValidator,
        }),
        product: useFormik<FormikProductProps>({
            initialValues: initialProductValues,
            onSubmit: values => {
                // console.log(values);
                navigate("to");
            },
        }),
        to: useFormik<UpdateFormikToProps>({
            initialValues: initialUpdateToValues,
            onSubmit: (values, { setStatus }) => {
                // console.log("values", values);
                const delivery_date = format(formatDisplayDateStringToDate(values.delivery_date), "yyyy-MM-dd");
                const isSpecialDeliveryHour = values.delivery_hour === "오전중" || values.delivery_hour === "오후중" || values.delivery_hour === "당일배송";
                const delivery_time = isSpecialDeliveryHour
                    ? values.delivery_hour
                    : format12To24(values.delivery_hour, values.delivery_minute) + " " + values.delivery_type;
                const body: OrderPutRequest = {
                    order_name: formik.from.values.order_name,
                    order_mobile: formik.from.values.order_mobile,
                    product_idx: Number(formik.product.values.product_idx),
                    wreath_sender: values.wreath_sender, //명의
                    wreath_word: values.wreath_word, //경조사어
                    recipient_name: values.recipient_name,
                    recipient_mobile: values.recipient_mobile,
                    is_union: values.is_union,
                    event_content_idx: values.event_content_idx ?? 1, //경조내용 index
                    related_person: values.related_person,
                    work_location: values.work_location, //근무지
                    department: values.department, //부서
                    company_tel: values.company_tel, //사번
                    delivery_immediate: values.delivery_immediate, //즉시 배송 여부
                    delivery_date: delivery_date, //배송일자 (YYYY-MM-DD)
                    delivery_time: values.delivery_immediate ? "" : delivery_time,
                    delivery_post: values.delivery_post,
                    delivery_address: values.delivery_address,
                    delivery_address_detail: values.delivery_address_detail,
                    delivery_memo_idx: values.delivery_memo_idx!, //배송 메모 index
                    delivery_memo: values.delivery_memo_idx === 1 ? values.display_delivery_memo : values.delivery_memo, //배송 메모
                    file_memo: values.file_memo, //초대장, 청첩장, 부고장 정보 메모
                    order_memo: values.order_memo, //주문 메모
                    order_file: values.order_file,
                    del_file_idx: values.del_file_idx ?? [],
                };
                // console.log("body", body);
                // return;
                mutate(
                    { ...body },
                    {
                        onSuccess: data => {
                            navigate(`/orders/${params.idx}`);
                        },
                        onError: error => {
                            const err_msg = errorMsgHandler(error);
                            setStatus({
                                state: "error",
                                msg: err_msg,
                            });
                        },
                    },
                );
            },
            validate: orderToValidator,
        }),
    };

    useEffect(() => {
        if (data && productInfo) {
            formik.from.setValues({
                customer_name: data?.member_info.customer_name ?? "",
                customer_idx: data?.member_info.customer_idx!,
                order_name: data?.member_info.order_name ?? "",
                order_mobile: data?.member_info.order_mobile ?? "",
            });
            formik.product.setValues({
                category_idx: category_idx!,
                product_idx: product_idx!,
                union_option: productInfo.product_category.union_option,
            });
            if (data?.recipient_info) {
                // console.log(data?.recipient_info.delivery_time); //오후 14시 30분 까지
                // const time = data?.recipient_info.delivery_immediate ? "" : formatDeliveryTime(data?.recipient_info.delivery_time);
                const time = data?.recipient_info.delivery_immediate ? "" : data?.recipient_info.delivery_time;
                const isSpecialDeliveryHour = time === "오전중" || time === "오후중" || time === "당일배송";
                const delivery_hour = data?.recipient_info.delivery_immediate ? "" : isSpecialDeliveryHour ? time : time.split(" ")[0] + " " + time.split(" ")[1]; // 임의 추가
                const delivery_minute = data?.recipient_info.delivery_immediate ? "" : isSpecialDeliveryHour ? "" : time.split(" ")[2]; // 임의 추가
                const delivery_type = data?.recipient_info.delivery_immediate ? "" : isSpecialDeliveryHour ? "" : time.split(" ")[3]; // 임의 추가

                formik.to.setValues({
                    // customer_name: ,
                    // customer_idx: ,
                    // order_name: ,
                    // order_mobile: "",
                    // product_idx: data?.product_info.product_idx!,
                    wreath_sender: data?.recipient_info.wreath_sender ?? "",
                    wreath_word: data?.recipient_info.wreath_word ?? "",
                    recipient_name: data?.recipient_info.recipient_name ?? "",
                    recipient_mobile: data?.recipient_info.recipient_mobile ?? "",
                    is_union: data?.recipient_info.is_union!,
                    event_content_idx: data?.recipient_info.event_content.event_content_idx ?? null,
                    depth1_idx: data?.recipient_info.event_content.depth1_idx ?? null, // 임의 추가
                    event_content: data?.recipient_info.event_content.depth2 ?? "", // 임의 추가
                    related_person: data?.recipient_info.related_person ?? "",
                    work_location: data?.recipient_info.work_location ?? "",
                    department: data?.recipient_info.department ?? "",
                    company_tel: data?.recipient_info.company_tel ?? "",
                    delivery_immediate: data?.recipient_info.delivery_immediate,
                    delivery_date: data?.recipient_info.delivery_date!,
                    original_delivery_date: data?.recipient_info.delivery_date!,
                    delivery_time: data?.recipient_info.delivery_immediate ? "" : data?.recipient_info.delivery_time, // 임의 추가,
                    delivery_hour: delivery_hour,
                    delivery_minute: delivery_minute,
                    delivery_type: delivery_type,
                    original_delivery_hour: delivery_hour, // 임의 추가
                    original_delivery_minute: delivery_minute, // 임의 추가
                    original_delivery_type: delivery_type, // 임의 추가
                    delivery_post: data?.recipient_info.delivery_post!,
                    delivery_address: data?.recipient_info.delivery_address ?? "",
                    delivery_address_detail: data?.recipient_info.delivery_address_detail ?? "",
                    delivery_memo_idx: data?.recipient_info.delivery_memo_idx ?? null,
                    delivery_memo: data?.recipient_info.delivery_memo ?? "",
                    display_delivery_memo: data?.recipient_info.delivery_memo ?? "",
                    file_memo: data?.recipient_info.file_memo ?? "",
                    order_memo: data?.recipient_info.order_memo ?? "",
                    order_file: data?.recipient_info.order_file ?? [],
                    del_file_idx: [],
                    is_visible_event_content_field: false,
                });
            }
        }
    }, [data, productInfo]);

    // 개인회원은 사용할수 없음
    const { data: senderData } = useGetCommonSender(isIndividualCustomer ? "" : customer_idx?.toString(), "", {
        // enabled: !isIndividualCustomer && !!customer_idx,
    });
    // console.log("senderData", senderData);

    // 회사/비회원 회원에 등록된 명의/경조사어
    useEffect(() => {
        const isUnionCategory = formik.to.values.is_union;
        const targetSenderData = senderData && senderData.data.find(data => data.union_option);

        if (isUnionCategory && targetSenderData) {
            formik.to.setValues({
                ...formik.to.values,
                wreath_sender: targetSenderData.union_sender, // 명의
                wreath_word: targetSenderData.default_sender, // 경조사어
            });
        }
    }, [formik.to.values.is_union, senderData]);

    return (
        <>
            {isMatchedRootPages && <Navigate to={`/orders/${params.idx}/update/from`} replace />}
            <Outlet context={{ formik, isSubmitting: isPending }} />
        </>
    );
}
